import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { UserService } from 'src/app/shared/services/user.service';
import { AuthService } from '@app/services/authentication/auth.service';
import { MsalService } from '@azure/msal-angular';
import { ResourceService } from '@app/services/resources.service';
import { ThemeDisplayService } from '@app/services/theme-display.service';
import { Roles, User } from '@app/models/user';
import { ApplicationServiceStatus, ApplicationStatus } from '@app/models/monitoring';
import { LocalStorage, RegistrationTool, Segment } from '@app/models/constants';
import {
	ClrDatagridModule,
	ClrDropdownModule,
	ClrModalModule,
	ClrNavigationModule,
	ClrSpinnerModule
} from '@clr/angular';
import { CommonModule } from '@angular/common';
import { ToCamelPipe } from '@app/shared/pipes/to-camel.pipe';
import { CdsIconModule } from '@cds/angular';
import { environment } from '@env/environment';

const { version: appVersion } = require('../../../../../../package.json');

declare let require: any;

@Component({
	selector: 'app-top-menu',
	templateUrl: './application-top-menu.component.html',
	styleUrls: ['./application-top-menu.component.scss'],
	imports: [
		CommonModule,
		ClrNavigationModule,
		ClrDropdownModule,
		TranslateModule,
		ToCamelPipe,
		ClrModalModule,
		ClrSpinnerModule,
		ClrDatagridModule,
		CdsIconModule,
		RouterLink
	],
	standalone: true
})
export class ApplicationTopMenuComponent implements OnInit {
	public appVersion;
	userDetails: User;
	lang = 'fr';
	public readonly ENVIRONMENT = environment;

	openMonitoring = false;
	applicationStatus = { status: 'unknown' } as ApplicationStatus;
	statusRefresh = false;

	constructor(
		private router: Router,
		private toastr: ToastrService,
		public translate: TranslateService,
		private resourceService: ResourceService,
		public userService: UserService,
		private themeDisplayService: ThemeDisplayService,
		private authService: AuthService,
		private msalService: MsalService,
		private destroyRef: DestroyRef
	) {
		this.appVersion = appVersion;
	}

	/**
	 * Reactive subscription (NOT a one-time `userService.getUser()` snapshot) — this component
	 * can mount before `AppComponent`'s async `MsalBroadcastService.inProgress$` subscription has
	 * called `setUserFromMsal()` (see cognito-to-msal-migration skill lesson #6/#19: user
	 * population is now asynchronous, unlike the old Cognito `APP_INITIALIZER` flow). A one-time
	 * snapshot captured `null` in that race and never updated, leaving name/role permanently blank.
	 */
	ngOnInit() {
		this.userService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
			if (!user) {
				return;
			}
			this.userDetails = user;
			this.toastr.info('Welcome back ' + this.userDetails.firstName);

			if (this.userDetails.username && this.userDetails.username !== '') {
				localStorage.setItem('sAMAccountName', this.userDetails.username);
			}
		});

		if (localStorage.getItem(LocalStorage.MARKET) != null) {
			this.doChangeMarket(localStorage.getItem(LocalStorage.MARKET));
		} else {
			this.doChangeMarket(Segment.B2C);
		}
		if (localStorage.getItem(LocalStorage.LANGUAGE) != null) {
			this.translate.use(localStorage.getItem(LocalStorage.LANGUAGE));
			this.lang = localStorage.getItem(LocalStorage.LANGUAGE);
		}
	}

	hasRoles() {
		return this.userService.hasRole(Roles.BUSINESS_ADMIN) || this.userService.hasRole(Roles.INTERNAL_BACKOFFICE_EXPERT);
	}

	statusColor(item: ApplicationServiceStatus) {
		if ('CHECK_OK' === item.status) {
			return 'GREEN';
		} else if ('UNCHECKED' === item.status) {
			return 'ORANGE';
		}
		return 'RED';
	}

	doLogout() {
		localStorage.removeItem('sAMAccountName');
		this.authService.logout();
	}

	doOpenAndRefreshStatus() {
		this.statusRefresh = true;
		this.resourceService.getApplicationStatus().subscribe((value) => {
			this.applicationStatus = value;
			this.statusRefresh = false;
		});
		this.openMonitoring = true;
	}

	doChangeMarket(market: string) {
		if (this.currentMarket() !== market) {
			const currentTool: string = this.currentTool();
			this.themeDisplayService.displayMarketTheme(market, currentTool);
			this.router.navigate([`/${currentTool}/`]);
		}
	}

	doChangeLang(language: string) {
		this.lang = language;
		localStorage.setItem('language', language);
		this.translate.use(language);
	}

	currentMarket(): string {
		return localStorage.getItem(LocalStorage.MARKET) != null ? localStorage.getItem(LocalStorage.MARKET) : Segment.B2C;
	}

	currentTool(): string {
		return localStorage.getItem(LocalStorage.TOOLS) != null
			? localStorage.getItem(LocalStorage.TOOLS)
			: RegistrationTool.REGISTRATIONS;
	}

	async getJWTToken() {
		const account = this.msalService.instance.getActiveAccount();
		if (!account) {
			this.toastr.error('No active MSAL account — cannot acquire a token');
			return;
		}
		try {
			const result = await this.msalService.instance.acquireTokenSilent({
				scopes: environment.apiConfig.scopes,
				account
			});
			await navigator.clipboard.writeText(result.accessToken);
			this.toastr.info('Access token copied to clipboard');
		} catch (error) {
			console.error('Could not acquire access token', error);
			this.toastr.error('Could not acquire access token');
		}
	}
}
